import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import moment from 'moment';
import { HistoricMarketPriceItem } from '../../../../types/productType';
import { toChartXYPoint } from '../../../../utils';
import { AppContext } from '../../../../context/ContextProvider';
import { useTheme } from '../../../../context/ThemeContext';

interface LiveExPerformanceChartProps {
  classNameContainer?: string;
  chartData: HistoricMarketPriceItem[];
}

const CHART_HEIGHT = 220;
const MOUNT_DELAY = 100; // Brief delay for slideout to start opening

const getChartConfig = (isDark: boolean): Highcharts.Options => {
  const colors = {
    label: isDark ? '#94a3b8' : '#6b7280',
    gridLine: isDark ? '#475569' : '#e5e7eb',
    line: isDark ? '#5eead4' : '#1D4854', // teal-300 for dark, vine for light
    fillGradient: (isDark
      ? [
          [0, 'rgba(94, 234, 212, 0.25)'],
          [0.5, 'rgba(94, 234, 212, 0.1)'],
          [1, 'rgba(94, 234, 212, 0)'],
        ]
      : [
          [0, 'rgba(29, 72, 84, 0.25)'],
          [0.5, 'rgba(29, 72, 84, 0.1)'],
          [1, 'rgba(29, 72, 84, 0)'],
        ]) as [number, string][],
    crosshair: isDark ? 'rgba(94, 234, 212, 0.3)' : 'rgba(29, 72, 84, 0.3)',
    tooltipBg: isDark ? '#334155' : '#1D4854',
  };

  return {
    chart: {
      type: 'areaspline',
      plotBackgroundColor: undefined,
      plotBackgroundImage: undefined,
      plotBorderWidth: 0,
      plotShadow: false,
      backgroundColor: 'transparent',
      spacing: [10, 15, 10, 15],
      style: {
        fontFamily: 'inherit',
      },
    },
    accessibility: {
      enabled: false,
    },
    title: {
      text: '',
    },
    legend: {
      enabled: false,
    },
    yAxis: {
      title: {
        text: '',
      },
      labels: {
        enabled: true,
        style: {
          color: colors.label,
          fontSize: '10px',
        },
        formatter: function () {
          const val = this.value as number;
          if (val >= 1000) {
            return `£${(val / 1000).toFixed(val >= 10000 ? 0 : 1)}k`;
          }
          return `£${val}`;
        },
      },
      gridLineColor: colors.gridLine,
      gridLineDashStyle: 'Dot',
      gridLineWidth: 1,
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      type: 'datetime',
      lineColor: colors.gridLine,
      tickColor: colors.gridLine,
      crosshair: {
        color: colors.crosshair,
        dashStyle: 'Dash',
        width: 1,
      },
      labels: {
        style: {
          color: colors.label,
          fontSize: '10px',
        },
        formatter: function () {
          return moment(this.value).format("MMM 'YY");
        },
      },
      tickInterval: 365 * 24 * 3600 * 1000, // One year
    },
    tooltip: {
      shared: true,
    },
    plotOptions: {
      areaspline: {
        fillOpacity: 0.15,
        lineWidth: 2.5,
        states: {
          hover: {
            lineWidth: 3,
          },
        },
        marker: {
          enabled: false,
          states: {
            hover: {
              enabled: true,
              radius: 5,
              lineWidth: 2,
              lineColor: isDark ? '#1e293b' : '#ffffff',
              fillColor: colors.line,
            },
          },
        },
      },
    },
    series: [
      {
        type: 'areaspline',
        name: 'Market Price',
        color: colors.line,
        fillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: colors.fillGradient,
        },
        data: [],
      },
    ],
    _themeColors: colors, // Store for use in tooltip
  } as Highcharts.Options;
};

const ChartLoadingPlaceholder = ({ isDark }: { isDark: boolean }) => (
  <div className="flex items-center justify-center" style={{ height: CHART_HEIGHT }}>
    <svg
      className={`animate-spin h-5 w-5 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  </div>
);

export default function LiveExPerformanceChart({ classNameContainer, chartData }: LiveExPerformanceChartProps) {
  const { formatter: currencyFormatter } = useContext(AppContext);
  const { isDark } = useTheme();
  const chartRef = useRef<Highcharts.Chart | null>(null);
  const [isReady, setIsReady] = useState(false);

  // Brief delay then mount chart; reflow after slideout animation finishes
  useEffect(() => {
    const mountTimer = setTimeout(() => setIsReady(true), MOUNT_DELAY);
    // Reflow after slideout animation (300ms) completes to fix width
    const reflowTimer = setTimeout(() => {
      chartRef.current?.reflow();
    }, 350);
    return () => {
      clearTimeout(mountTimer);
      clearTimeout(reflowTimer);
    };
  }, []);

  // Compute chart options synchronously so chart always renders with correct config
  const chartOptions = useMemo((): Highcharts.Options => {
    const baseConfig = getChartConfig(isDark);

    if (!chartData || chartData.length === 0) return baseConfig;

    const performanceData = toChartXYPoint(chartData, 'date', 'marketPrice', false)
      .map((point) => {
        const dateObj = moment(point.x);
        return { x: dateObj.toDate().getTime(), y: point.y } as { x: number; y: number };
      })
      .sort((a, b) => a.x - b.x);

    const prices = performanceData.map((p) => p.y);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const padding = (maxPrice - minPrice) * 0.1;

    const tooltipBg = isDark ? '#334155' : '#1D4854';

    const firstDate = performanceData[0].x;
    const lastDate = performanceData[performanceData.length - 1].x;
    const dateRange = lastDate - firstDate;
    const xPadding = dateRange * 0.03;

    return {
      ...baseConfig,
      yAxis: {
        ...baseConfig.yAxis,
        min: Math.max(0, minPrice - padding),
        max: maxPrice + padding,
        labels: {
          ...(baseConfig.yAxis as Highcharts.YAxisOptions).labels,
          formatter: function () {
            const val = this.value as number;
            if (val >= 1000) {
              return currencyFormatter.format(val, true).replace(/\.00$/, '');
            }
            return currencyFormatter.format(val, true);
          },
        },
      },
      xAxis: {
        ...baseConfig.xAxis,
        min: firstDate - xPadding,
        max: lastDate + xPadding,
        startOnTick: false,
        endOnTick: false,
      },
      tooltip: {
        ...baseConfig.tooltip,
        formatter: function () {
          const point = this.points?.[0];
          if (!point) return '';
          const date = moment(point.x).format('MMM YYYY');
          const price = currencyFormatter.format(point.y as number, true);
          return `<div style="padding: 8px 12px; font-size: 12px;">
            <div style="color: #9ca3af; margin-bottom: 4px;">${date}</div>
            <div style="font-weight: 600; font-size: 14px;">${price}</div>
          </div>`;
        },
        useHTML: true,
        backgroundColor: tooltipBg,
        borderWidth: 0,
        borderRadius: 8,
        shadow: true,
        style: {
          color: '#ffffff',
        },
      },
      series: [
        {
          ...baseConfig.series![0],
          data: performanceData,
        } as Highcharts.SeriesOptionsType,
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData, isDark]);

  const hasData = chartData && chartData.length > 0;

  // Always show loading spinner until container is at final width
  if (!isReady) {
    return (
      <div className={`flex flex-col w-full ${classNameContainer || ''}`.trim()}>
        <ChartLoadingPlaceholder isDark={isDark} />
      </div>
    );
  }

  // After ready, show empty state if no data
  if (!hasData) {
    return (
      <div className={`flex flex-col w-full items-center justify-center py-8 ${classNameContainer || ''}`.trim()}>
        <div className="text-gray-400 text-sm">No price history available</div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col w-full ${classNameContainer || ''}`.trim()}>
      <HighchartsReact
        highcharts={Highcharts}
        options={chartOptions}
        callback={(chart: Highcharts.Chart) => {
          chartRef.current = chart;
        }}
        containerProps={{ style: { height: CHART_HEIGHT, width: '100%' } }}
      />
    </div>
  );
}
